import { ErrorFactory } from "@/utils/error"
import { PayloadFormatter } from "@/utils/format"
import { createLogger } from "@/utils/logger"
import { signalWithPostmanApi } from "@/utils/signal_utils"
import { computed, signal } from "@preact/signals"
import dayjs from "dayjs"
import Apis from "."
import { getCurrentTagByDomain } from "@/utils/common"
import { isArray, isPlainObject } from "lodash"
import AppSignals from "@/features_signal/app"

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void
        userID?: string
    }
}

const logger = createLogger("ApiSignals")

const [dataSignalSendOtp, fetchDataSignalSendOtp] = signalWithPostmanApi<
    {
        phoneNumber: string
        countryCode?: string
    },
    {
        id: string
        codeLength: number
        resendAt: number
    }
>(
    async (props) => {
        const phone = `${props?.phoneNumber?.match(/9\d+/g)?.[0] ?? ""}`
        const country_code = props?.countryCode?.replace("+", "") ?? "63"

        const response = await Apis.v1.sendSmsOtp({
            phone,
            country_code,
        })

        const result: {
            id: string
            codeLength: number
            resendAt: number
        } = {
            id: response.data.data?.id ?? "",
            codeLength: response.data.data?.codeLength ?? 6,
            resendAt: response.data.data?.resendAt ?? 0,
        }
        return result
    },
    {
        storageKey: "tmp_otp_object",
        storage: sessionStorage,
    },
)

const [dataSignalAccessToken, fetchDataSignalLoginWithOtp] = signalWithPostmanApi<
    {
        otpId: string
        otpCode: string
    },
    string
>(
    async (props) => {
        if (!props) {
            return ""
        }
        const response = await Apis.v1.loginWithSmsOtpCode({
            otp_id: props.otpId,
            code: props.otpCode,
        })

        if (!response.data.data) {
            throw ErrorFactory.createAuthenticationError("Failed to login with OTP", response.data)
        }
        return response.data.data
    },
    {
        storageKey: "access_token",
        storage: sessionStorage,
    },
)

const [, fetchDataSignalLoginWithPassword] = signalWithPostmanApi<
    {
        countryCode: string
        number: string
        password: string
    },
    string
>(
    async (props) => {
        if (!props) {
            return ""
        }
        const response = await Apis.v1.loginWithPassword({
            country_code: props.countryCode,
            phone: props.number,
            password: props.password,
        })

        if (!response.data.data) {
            throw ErrorFactory.createAuthenticationError(
                "Failed to login with password",
                response.data,
            )
        }
        return response.data.data
    },
    {
        storageKey: "access_token",
        storage: sessionStorage,
        connectSignal: dataSignalAccessToken,
    },
)

const [, fetchDataSignalRefreshToken] = signalWithPostmanApi(Apis.v1.refreshToken)

const [dataSignalProfile, fetchDataSignalProfile] = signalWithPostmanApi<
    never,
    Partial<ApiTypes.Fetcher.Result.UserProfile>
>(
    async () => {
        const response = await Apis.v1.refGetProfile()
        const data = response.data.data // 设置用户ID到GA4
        const userId = data?.uid || ""
        if (userId && window.setGA4UserId) {
            window.setGA4UserId(userId)
        }

        if (!data) {
            return {}
        }

        const real_username = PayloadFormatter.jsonParse(data?.real_username) as {
            first_name: string
            middle_name: string
            last_name: string
        }
        const result: Required<ApiTypes.Fetcher.Result.UserProfile> = {
            uid: data?.uid ?? "",
            username: data?.username ?? "",
            email: data?.email ?? "",
            email_check_state: data?.email_check_state ?? 0,
            country_code: data?.country_code ?? "",
            phone: data?.phone ?? "",
            phone_check_state: data?.phone_check_state ?? 0,
            avatar: data?.avatar ?? "",
            gender: data?.gender ?? "",
            password: data?.password ?? "",
            //@ts-ignore
            oauth_secrets: data?.oauth_secrets ?? "",
            referral_code: data?.referral_code ?? "",
            tags: data?.tags ?? "",
            vip: data?.vip ?? 0,
            parent_uid: data?.parent_uid ?? "",
            //@ts-ignore
            parent_name: data?.parent_name ?? "",
            tester: data?.tester ?? 0,
            device_number: data?.device_number ?? "",
            //@ts-ignore
            state: data?.state ?? 0,
            note: data?.note ?? "",
            created_at: data?.created_at ?? 0,
            created_ip: data?.created_ip ?? "",
            reg_ip: data?.reg_ip ?? "",
            reg_device: data?.reg_device ?? 0,
            reg_device_no: data?.reg_device_no ?? "",
            last_login_ip: data?.last_login_ip ?? "",
            last_login_at: data?.last_login_at ?? 0,
            last_login_device: data?.last_login_device ?? 0,
            nick_name: data?.nick_name ?? "",
            updated_at: data?.updated_at ?? 0,
            hasLoginPassword: data?.has_login_password ?? false,
            hasEmail: data?.has_email ?? false,
            hasPhoneNumber: data?.has_phone_number ?? false,
            realName: {
                firstName: real_username.first_name ?? "",
                middleName: real_username.middle_name ?? "",
                lastName: real_username.last_name ?? "",
            },
            phoneNumber: {
                number: data?.phone ?? "",
                countryCode: data?.country_code ?? "",
            },
            has_email: data?.has_email ?? false,
            hasEmailVerified: false,
            nickname: data?.nick_name ?? "",
            // @ts-ignore
            kyc_status: data?.kyc_status ?? 0,
            xp: Number(data?.xp ?? 0),
            withdraw_count: Number(data?.withdraw_count ?? 0),
            withdraw_total: Number(data?.withdraw_total ?? 0),
            third_deposit: Number(data?.third_deposit ?? 0),
            third_party: data?.third_party ?? "",
        }
        return result
    },
    {
        storageKey: "user_profile",
        storage: sessionStorage,
    },
)

const [dataSignalVipRule, fetchDataSignalVipRule] = signalWithPostmanApi<
    never,
    Partial<MemberApiTypes.Fetcher.Result.RefGetVipRuleResult>
>(
    async () => {
        const response = await Apis.v1.refGetVipRule()
        const data = response.data
        if (!data) {
            return {}
        }
        const transformVipLevels = (
            levels:
                | Array<{
                      level?: number
                      ty?: string
                      xp?: string
                      upgrade?: string
                      nextUpgrade?: number
                      birthday?: string
                      multiple?: string
                      img?: string
                  }>
                | undefined,
        ) => {
            const result: MemberApiTypes.Fetcher.Result.VipLevel[] =
                levels?.map((level) => ({
                    level: level.level ?? 0,
                    ty: (level.ty ?? "Bronze") as MemberApiTypes.Fetcher.Result.VipType,
                    type: (level.ty ?? "Bronze") as MemberApiTypes.Fetcher.Result.VipType,
                    xp: Number(level.xp) || 0,
                    upgrade: Number(level.upgrade) || 0,
                    birthday: Number(level.birthday) || 0,
                    multiple: Number(level.multiple) || 0,
                    img: level.img ?? "",
                })) || []

            return result
        }

        const result: Partial<MemberApiTypes.Fetcher.Result.RefGetVipRuleResult> = {
            data: {
                Bronze: transformVipLevels(data.data?.Bronze),
                Silver: transformVipLevels(data.data?.Silver),
                Gold: transformVipLevels(data.data?.Gold),
                Platinum: transformVipLevels(data.data?.Platinum),
                Diamond: transformVipLevels(data.data?.Diamond),
            },
        }
        return result
    },
    {
        storageKey: "vip_rule",
    },
)

const [, fetchDataSignalLogout] = signalWithPostmanApi<never, void>(async () => {
    const response = await Apis.v1.logout()
    response.data.data
})

const [dataSignalKyc, fetchDataSignalKyc] = signalWithPostmanApi<never, Partial<Kyc.Data.Detail>>(
    async () => {
        const response = await Apis.v1.refGetKyc()
        const data = response.data.data

        if (!data) {
            return {}
        }

        const result: Partial<Kyc.Data.Detail> = {
            uid: data.uid,
            created_at: data.created_at,
            updated_at: data.updated_at,
            country_code: data.country_code,
            phone: data.phone,
            first_name: data.first_name,
            middle_name: data.middle_name,
            last_name: data.last_name,
            id_type: data.id_type as Kyc.IdType,
            id_number: data.id_number,
            birthday: data.birthday,
            nearest_branch: data.nearest_branch,
            gender: data.gender as Kyc.Gender,
            attachments_face: data.attachments?.face,
            attachments_faceTmpUrl: data.attachments?.faceTmpUrl,
            attachments_idPhoto: data.attachments?.idPhoto,
            attachments_idPhotoTmpUrl: data.attachments?.idPhotoTmpUrl,
            attachments_selfieWithIDPhotoPath: data.attachments?.selfieWithIDPhotoPath,
            // @ts-ignore
            attachments_selfieWithIDPhotoPathTmpUrl: data.attachments?.selfieWithIDPhotoPathTmpUrl,
            extra_details_issuing_authority: data.extra_details?.issuing_authority,
            extra_details_expiry_date: data.extra_details?.expiry_date,
            place_of_birth: data.place_of_birth,
            nationality: data.nationality,
            current_address: data.current_address,
            permanent_address: data.permanent_address,
            nature_of_work: data.nature_of_work,
            source_of_income: data.source_of_income,
            occupation: data.occupation,
            tokens: data.tokens,
            blacklist_status: data.blacklist_status as Kyc.BlacklistStatus,
            issue_message: data.issue?.message,
            issue_msg: data.issue_msg,
            issue_fields: data.issue_fields,
            status: (data.status || "unset") as Kyc.Status,
            comment: data.comment,
            reviewer: data.reviewer,
            review_times: data.review_times,
            ocr_status: data.ocr_status as Kyc.OcrStatus,
            ocr_realName_firstName: data.ocr?.realName?.firstName,
            ocr_realName_middleName: data.ocr?.realName?.middleName,
            ocr_realName_lastName: data.ocr?.realName?.lastName,
            ocr_birthday: data.ocr?.birthday,
            ocr_idType: data.ocr?.idType as Kyc.IdType,
            ocr_idNumber: data.ocr?.idNumber,
            ocr_gender: data.ocr?.gender as Kyc.Gender,
            ocr_attachments_face: data.ocr?.attachments?.face,
            ocr_attachments_idPhoto: data.ocr?.attachments?.idPhoto,
            ocr_extraDetails_confidence_score: data.ocr?.extraDetails?.confidence_score,
            ocr_nationality: data.ocr?.nationality,
            ocr_address: data.ocr?.address,
            ocr_verified: data.ocr?.verified,
            version: data.version,
        }
        return result
    },
    {
        storageKey: "kyc",
    },
)

// 获取门店列表
const [dataSignalShopList, fetchDataSignalShopList] = signalWithPostmanApi<
    never,
    { label: string; value: string }[]
>(
    async () => {
        const response = await Apis.v1.refGetShopList()
        const data = response.data.data

        if (!data || !Array.isArray(data)) {
            return []
        }

        return data.map((shop) => ({
            label: shop.address || "",
            value: shop.id?.toString() || "",
        }))
    },
    {
        storageKey: "shop_list",
        storage: sessionStorage,
    },
)

//获取场馆列表
const [dataSignalGamePlatform, fetchDataSignalGamePlatform] = signalWithPostmanApi<
    Partial<GameApiTypes.Fetcher.Props.GamePlatformProps>,
    Partial<GameApiTypes.Fetcher.Result.GamePlatformResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetGamePlatform(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: Partial<GameApiTypes.Fetcher.Result.GamePlatformResult> = {
            data: data.data?.d,
            s: data.data?.s,
            t: data.data?.t,
        }
        return result
    },
    {
        storageKey: "game_platform",
    },
)

//获取游戏列表
const [dataSignalGameList, fetchDataSignalGameList] = signalWithPostmanApi<
    Partial<GameApiTypes.Fetcher.Props.GameListProps>,
    Partial<GameApiTypes.Fetcher.Result.GameListResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetGameList(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: Partial<GameApiTypes.Fetcher.Result.GameListResult> = {
            data: data.data?.d,
            s: data.data?.s,
            t: data.data?.t,
        }
        return result
    },
    {
        storageKey: "game_list",
    },
)

//添加游戏收藏
const [dataSignalAddGameFav, fetchDataSignalAddGameFav] = signalWithPostmanApi<
    Partial<GameApiTypes.Fetcher.Props.AddGameFavProps>,
    Partial<GameApiTypes.Fetcher.Result.AddGameFavResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetAddGameFav(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: Partial<GameApiTypes.Fetcher.Result.AddGameFavResult> = {
            data: data.data,
            status: data.status,
        }
        return result
    },
    {
        storageKey: "add_game_fav",
    },
)

//获取游戏收藏列表
const [dataSignalGameFavList, fetchDataSignalGameFavList] = signalWithPostmanApi<
    Partial<GameApiTypes.Fetcher.Props.GameFavListProps>,
    Partial<GameApiTypes.Fetcher.Result.GameFavListResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetGameFavList(props)
        const data = response.data
        if (!data) {
            return {
                data: [],
                s: 0,
                t: 0,
            }
        }
        const result: Partial<GameApiTypes.Fetcher.Result.GameFavListResult> = {
            data: data.data?.d,
            s: data.data?.s,
            t: data.data?.t,
        }
        logger.debug("fetchDataSignalGameFavList", result)
        return result
    },
    {
        storageKey: "game_fav_list",
    },
)

//删除游戏收藏
const [dataSignalDeleteGameFav, fetchDataSignalDeleteGameFav] = signalWithPostmanApi<
    Partial<GameApiTypes.Fetcher.Props.DeleteGameFavProps>,
    Partial<GameApiTypes.Fetcher.Result.DeleteGameFavResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetDeleteGameFav(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: Partial<GameApiTypes.Fetcher.Result.DeleteGameFavResult> = {
            data: data.data,
            status: data.status,
        }
        return result
    },
    {
        storageKey: "delete_game_fav",
    },
)

//获取游戏投注列表
const [dataSignalGameBetList, fetchDataSignalGameBetList] = signalWithPostmanApi<
    Partial<GameApiTypes.Fetcher.Props.GameBetListProps>,
    Partial<GameApiTypes.Fetcher.Result.GameBetListResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetGameBetList(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: Partial<GameApiTypes.Fetcher.Result.GameBetListResult> = {
            bt: data.data?.bt,
            wt: data.data?.wt,
            t: data.data?.t,
            d: data.data?.d,
        }
        return result
    },
    {
        storageKey: "game_bet_list",
    },
)

//获取游戏启动
const [dataSignalGameLaunch, fetchDataSignalGameLaunch] = signalWithPostmanApi<
    Partial<GameApiTypes.Fetcher.Props.GameLaunchProps>,
    Partial<GameApiTypes.Fetcher.Result.GameLaunchResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetGameLaunch(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: Partial<GameApiTypes.Fetcher.Result.GameLaunchResult> = {
            data: data.data,
            status: data.status,
        }
        return result
    },
    {
        storageKey: "game_launch",
    },
)

const [walletSignal, fetchWalletSignal] = signalWithPostmanApi<
    never,
    ApiTypes.Fetcher.Result.RefUserWallet
>(async () => {
    const response = await Apis.v1.refGetFinanceWallet()
    const data = response.data.data
    if (!data) {
        return {}
    }
    const result: ApiTypes.Fetcher.Result.RefUserWallet = {
        uid: data.uid,
        balance: data.balance,
        withdrawable: data.withdrawable,
    }
    return result
})

const [dataSignalTransactionTypes, fetchDataSignalTransactionTypes] = signalWithPostmanApi<
    never,
    TransactionApiTypes.Fetcher.Types.TransactionType[]
>(async () => {
    const response = await Apis.v1.refGetTransactionTypes()
    const data = response.data.data
    if (!data) {
        return []
    }
    return data
})

//lazadamini登录
const [dataSignalLazadaminiLogin, fetchDataSignalLazadaminiLogin] = signalWithPostmanApi<
    Partial<FinanceApiTypes.Fetcher.Props.FinanceLazadaminiLoginProps>,
    Partial<FinanceApiTypes.Fetcher.Result.FinanceLazadaminiLoginResult>
>(async (props) => {
    if (!props) {
        return {}
    }
    try {
        const response = await Apis.v1.refFinanceLazadaminiLogin(props)
        const data = response.data.data
        if (!data) {
            return {}
        }
        const result: Partial<FinanceApiTypes.Fetcher.Result.FinanceLazadaminiLoginResult> = {
            token: data.token,
        }
        return result
    } catch (e) {
        return {}
    }
})

//账变记录
const [dataSignalTransactionList, fetchDataSignalTransactionList] = signalWithPostmanApi<
    Partial<FinanceApiTypes.Fetcher.Props.TransactionListProps>,
    Partial<FinanceApiTypes.Fetcher.Result.TransactionListResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetTransactionList(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.TransactionListResult = {
            data: data.data?.data,
            s: data.data?.s,
            t: data.data?.t,
        }
        return result
    },
    {
        storageKey: "transaction_list",
    },
)

//用户钱包
const [dataSignalFinanceWallet, fetchDataSignalFinanceWallet] = signalWithPostmanApi<
    never,
    FinanceApiTypes.Fetcher.Result.FinanceWalletResult
>(
    async () => {
        const response = await Apis.v1.refGetFinanceWallet()
        const data = response.data.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.FinanceWalletResult = {
            data: data,
        }
        return result
    },
    {
        storageKey: "finance_wallet",
    },
)

//用户账户列表
const [dataSignalFinanceAccountList, fetchDataSignalFinanceAccountList] = signalWithPostmanApi<
    never,
    FinanceApiTypes.Fetcher.Result.FinanceAccountListResult
>(
    async () => {
        const response = await Apis.v1.refGetFinanceAccountList()
        const data = response.data.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.FinanceAccountListResult = {
            data: data,
        }
        return result
    },
    {
        storageKey: "finance_account_list",
    },
)

//修改用户账户
const [dataSignalUpdateFinanceAccount, fetchDataSignalUpdateFinanceAccount] = signalWithPostmanApi<
    Partial<FinanceApiTypes.Fetcher.Props.UpdateFinanceAccountProps>,
    Partial<FinanceApiTypes.Fetcher.Result.UpdateFinanceAccountResult>
>(
    async (props) => {
        const response = await Apis.v1.refUpdateFinanceAccount({
            account: props?.account ?? "",
            channel_name: props?.channel_name ?? "",
            first_name: props?.first_name ?? "",
            last_name: props?.last_name ?? "",
            middle_name: props?.middle_name ?? "",
        })
        const data = response.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.UpdateFinanceAccountResult = {
            data: data.data,
            status: data.status,
        }
        return result
    },
    {
        storageKey: "update_finance_account",
    },
)

//删除用户账户
const [dataSignalDeleteFinanceAccount, fetchDataSignalDeleteFinanceAccount] = signalWithPostmanApi<
    Partial<FinanceApiTypes.Fetcher.Props.DeleteFinanceAccountProps>,
    Partial<FinanceApiTypes.Fetcher.Result.DeleteFinanceAccountResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetFinanceAccountDelete(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.DeleteFinanceAccountResult = {
            data: data.data,
            status: data.status,
        }
        return result
    },
    {
        storageKey: "delete_finance_account",
    },
)

//获取支付渠道列表
const [dataSignalFinanceChannelList, fetchDataSignalFinanceChannelList] = signalWithPostmanApi<
    Partial<FinanceApiTypes.Fetcher.Props.FinanceChannelListProps>,
    Partial<FinanceApiTypes.Fetcher.Result.FinanceChannelListResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetFinanceChannelList(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.FinanceChannelListResult = {
            data: data.data?.map((item) => ({
                ...item,
                amount_limit: item.amount_limit ?? [],
            })),
        }
        return result
    },
    {
        storageKey: "finance_channel_list",
    },
)

//充值
const [dataSignalFinancePaymentDeposit, fetchDataSignalFinancePaymentDeposit] =
    signalWithPostmanApi<
        Partial<FinanceApiTypes.Fetcher.Props.UpdateFinancePaymentDepositProps>,
        Partial<FinanceApiTypes.Fetcher.Result.UpdateFinancePaymentDepositResult>
    >(
        async (props) => {
            const response = await Apis.v1.refUpdateFinancePaymentDeposit(props)
            const data = response.data
            if (!data) {
                return {}
            }
            const result: FinanceApiTypes.Fetcher.Result.UpdateFinancePaymentDepositResult = {
                data: data.data,
                status: data.status,
            }
            return result
        },
        {
            storageKey: "finance_payment_deposit",
        },
    )
//提现
const [dataSignalFinancePaymentWithdraw, fetchDataSignalFinancePaymentWithdraw] =
    signalWithPostmanApi<
        Partial<FinanceApiTypes.Fetcher.Props.GetFinancePaymentWithdrawProps>,
        Partial<FinanceApiTypes.Fetcher.Result.GetFinancePaymentWithdrawResult>
    >(
        async (props) => {
            const response = await Apis.v1.refGetFinancePaymentWithdraw(props)
            const data = response.data
            if (!data) {
                return {}
            }
            const result: FinanceApiTypes.Fetcher.Result.GetFinancePaymentWithdrawResult = {
                data: data.data,
                status: data.status,
            }
            return result
        },
        {
            storageKey: "finance_payment_withdraw",
        },
    )
//充值记录
const [dataSignalFinanceDepositList, fetchDataSignalFinanceDepositList] = signalWithPostmanApi<
    Partial<FinanceApiTypes.Fetcher.Props.GetFinanceDepositListProps>,
    Partial<FinanceApiTypes.Fetcher.Result.GetFinanceDepositListResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetFinanceDepositList(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.GetFinanceDepositListResult = {
            data: data.data?.d,
            s: data.data?.s,
            t: data.data?.t,
        }
        return result
    },
    {
        storageKey: "finance_deposit_list",
    },
)
// 充值详情
const [dataSignalFinanceDepositDetail, fetchDataSignalFinanceDepositDetail] = signalWithPostmanApi<
    Partial<TransactionApiTypes.Fetcher.Request.GetFinanceDepositDetailProps>,
    Partial<TransactionApiTypes.Fetcher.Response.DepositDetail>
>(async (props) => {
    const response = await Apis.v1.refGetFinanceDepositDetail(props)
    const data = response.data.data
    if (!data) {
        return {}
    }
    const {
        status = "",
        amount = "",
        channel_name: method = "",
        channel_img: methodImg = "",
        created_at: timestamp = 0,
        id: transactionId = "",
    } = data
    const result: TransactionApiTypes.Fetcher.Response.DepositDetail = {
        type: "deposit",
        status,
        amount,
        method,
        methodImg,
        timestamp,
        transactionId,
    }
    logger.debug("dataSignalFinanceDepositDetail", result)
    return result
})
//提现记录
const [dataSignalFinanceWithdrawList, fetchDataSignalFinanceWithdrawList] = signalWithPostmanApi<
    Partial<FinanceApiTypes.Fetcher.Props.GetFinanceWithdrawListProps>,
    Partial<FinanceApiTypes.Fetcher.Result.GetFinanceWithdrawListResult>
>(
    async (props) => {
        const response = await Apis.v1.refGetFinanceWithdrawList(props)
        const data = response.data
        if (!data) {
            return {}
        }
        const result: FinanceApiTypes.Fetcher.Result.GetFinanceWithdrawListResult = {
            data: data.data?.d,
            s: data.data?.s,
            t: data.data?.t,
        }
        return result
    },
    {
        storageKey: "finance_withdraw_list",
    },
)
// 提现详情
const [dataSignalFinanceWithdrawDetail, fetchDataSignalFinanceWithdrawDetail] =
    signalWithPostmanApi<
        Partial<TransactionApiTypes.Fetcher.Request.GetFinanceWithdrawDetailProps>,
        Partial<TransactionApiTypes.Fetcher.Response.WithdrawDetail>
    >(async (props) => {
        const response = await Apis.v1.refGetFinanceWithdrawDetail(props)
        const data = response.data.data
        if (!data) {
            return {}
        }
        const {
            status = "",
            amount = "",
            account = "",
            channel_img: methodImg = "",
            created_at: timestamp = 0,
            id: transactionId = "",
        } = data
        const result: TransactionApiTypes.Fetcher.Response.WithdrawDetail = {
            type: "withdraw",
            status,
            account,
            methodImg,
            amount,
            timestamp,
            transactionId,
        }
        return result
    })

/**
 * @description 盲盒详情数据
 */
const [dataSignalBlindBox, fetchDataSignalBlindBox] = signalWithPostmanApi<
    Partial<BlindBoxApiTypes.Fetcher.Query.Params>,
    Partial<BlindBoxApiTypes.Fetcher.Response.Result>
>(async (param) => {
    const response = await Apis.v1.refFetchBlindboxDetail(param)
    const data = response.data.data
    if (!data) {
        return {}
    }
    const result: BlindBoxApiTypes.Fetcher.Response.Result = {
        claimed: data.claimed,
        data: data.data,
        total: data.total,
        unclaimed: data.unclaimed,
    }
    logger.debug("dataSignalBlindBox", result)
    return result
})

/**
 * @description 盲盒领取
 */
const [dataBlindBoxClaim, fetchDataBlindBoxClaim] = signalWithPostmanApi<
    Partial<BlindBoxApiTypes.Claim.Params>,
    Partial<BlindBoxApiTypes.Claim.Response>
>(async (data) => {
    if (!data) {
        return {}
    }
    const response = await Apis.v1.refFetchBlindboxClaim(data)
    return response.data
})

/**
 * @description 活动列表
 */
const [dataPromoList, fetchDataPromoList] = signalWithPostmanApi<
    Record<string, unknown>,
    PromoApiTypes.Fetcher.Result.PromoListItem[]
>(async () => {
    try {
        const {
            //@ts-ignore
            data: { data },
        } = await Apis.v1.refGetPromoList()
        return isArray(data) ? data : []
    } catch (error) {
        logger.debug("refGetPromoList error", error)
        return []
    }
})

/**
 * @description 首页数据
 */
const [dataSignalIndexCache, fetchDataSignalIndexCache] = signalWithPostmanApi<
    Record<string, unknown>,
    IndexCacheApiTypes.Fetcher.Result.IndexCacheResult
>(async () => {
    const tag = getCurrentTagByDomain()
    try {
        const ApiGetter = tag
            ? Apis.v1.refGetIndexCacheWithTag.bind(Apis.v1.refGetIndexCacheWithTag, tag)
            : Apis.v1.refGetIndexCache.bind(Apis.v1.refGetIndexCache)
        const cacheRes = await ApiGetter()
        const taggedData = cacheRes.data.data
        const result: IndexCacheApiTypes.Fetcher.Result.IndexCacheResult = {
            logo: isArray(taggedData?.logo) ? taggedData?.logo : [],
            top: isArray(taggedData?.top) ? taggedData?.top : [],
            banners: isArray(taggedData?.banners) ? taggedData?.banners : [],
            nav: taggedData?.nav || {},
            list: taggedData?.list || {},
            bottom: isArray(taggedData?.bottom) ? taggedData?.bottom : [],
            media: isArray(taggedData?.media) ? taggedData?.media : [],
            version: isArray(taggedData?.version) ? taggedData?.version : [],
        }
        return result
    } catch (error) {
        const result: IndexCacheApiTypes.Fetcher.Result.IndexCacheResult = {
            logo: [],
            top: [],
            banners: [],
            nav: {},
            list: {},
            bottom: [],
            media: [],
            version: [],
        }
        return result
    }
})

const brand_text = computed(() => {
    if (AppSignals.isMayaminiUser()) {
        return "maya"
    } else if (AppSignals.isLazadaminiUser()) {
        return "lazada"
    } else {
        return getCurrentTagByDomain() || "h5"
    }
})
// privacy,terms,faq
const [dataSignalPrivacy, fetchDataSignalPrivacy] = signalWithPostmanApi<
    {
        brand?: string
    },
    string
>(
    async () => {
        try {
            const response = await Apis.v1.refGetMemberPrivacy({
                brand: brand_text.value,
            })
            const content = response.data.data?.[0] ?? ""
            return content
        } catch (error) {
            return ""
        }
    },
    {
        storageKey: "privacy_policy",
        storage: sessionStorage,
    },
)

const [dataSignalTerms, fetchDataSignalTerms] = signalWithPostmanApi<
    {
        brand?: string
    },
    string
>(
    async () => {
        try {
            const response = await Apis.v1.refGetMemberTerms({
                brand: brand_text.value,
            })
            const content = response.data.data?.[0] ?? ""
            return content
        } catch (error) {
            return ""
        }
    },
    {
        storageKey: "terms_of_service",
        storage: sessionStorage,
    },
)

const [dataSignalFaq, fetchDataSignalFaq] = signalWithPostmanApi<
    {
        brand?: string
    },
    string
>(
    async () => {
        try {
            const response = await Apis.v1.refGetMemberFaq({
                brand: brand_text.value,
            })
            const content = response.data.data?.[0] ?? ""
            return content
        } catch (error) {
            return ""
        }
    },
    {
        storageKey: "faq_content",
        storage: sessionStorage,
    },
)

const [dataSignalAddAccounts, addDataSignalAccounts] = signalWithPostmanApi<
    FinanceAccountApiTypes.Fetcher.Query.AddAccountsParams,
    FinanceAccountApiTypes.Fetcher.Response.AddAccountsResult
>(async (props) => {
    if (!props) {
        console.log("Parameters are required")
        return {}
    }
    const response = await Apis.v1.refUpdateFinanceAccount(props)
    return response.data
})

const [dataSignalDeleteAccounts, deleteDataSignalAccounts] = signalWithPostmanApi<
    FinanceAccountApiTypes.Fetcher.Query.DeleteAccountsParams,
    FinanceAccountApiTypes.Fetcher.Response.AddAccountsResult
>(async (props) => {
    const response = await Apis.v1.refGetFinanceAccountDelete(props)
    return response.data
})

const [dataSignalFetchAccounts, fetchDataSignalAccounts] = signalWithPostmanApi<
    never,
    FinanceAccountApiTypes.Fetcher.Response.Result
>(async () => {
    const response = await Apis.v1.refGetFinanceAccountList()
    const data = response.data.data || []
    const accountList: FinanceAccountApiTypes.Fetcher.Response.Record[] = []
    data.map((it) => {
        accountList.push({
            account: it.account ?? "",
            channel_name: it.channel_name ?? "",
            currency: it.currency ?? "",
            first_name: it.first_name ?? "",
            id: it.id ?? "",
            last_name: it.last_name ?? "",
            middle_name: it.middle_name ?? "",
            status: it.status ?? 0,
            uid: it.uid ?? "",
        })
    })
    const result: FinanceAccountApiTypes.Fetcher.Response.Result = {
        list: accountList,
        status: response.data?.status ?? false,
    }
    return result
})

const [dataSignalWithDraw, fetchDataSignalWithdraw] = signalWithPostmanApi<
    FinanceAccountApiTypes.Fetcher.Query.WidthDrawParams,
    FinanceAccountApiTypes.Fetcher.Response.WithDrawResult
>(async (props) => {
    const response = await Apis.v1.refGetFinancePaymentWithdraw(props)
    return response.data
})

//埋点
const [dataSignalBuriedPointDetail, fetchDataSignalBuriedPointDetail] = signalWithPostmanApi<
    Partial<MemberApiTypes.Fetcher.Props.RefGetBuriedPointDetailProps>,
    Partial<MemberApiTypes.Fetcher.Result.RefGetBuriedPointDetailData>
>(async (props) => {
    const response = await Apis.v1.refGetBuriedpointDetail(props)
    const data = response.data.data

    if (!data) {
        return {}
    }

    const result: Partial<MemberApiTypes.Fetcher.Result.RefGetBuriedPointDetailData> = {
        ad_id: data.ad_id,
        ad_name: data.ad_name,
        channel: data.channel,
        config: data.config ? JSON.parse(data.config) : undefined,
        created_at: data.created_at,
        domain_id: data.domain_id,
        state: data.state,
        updated_at: data.updated_at,
        updated_name: data.updated_name,
        updated_uid: data.updated_uid,
    }

    return result
})

const ApiSignals = {
    dataPromoList,
    dataSignalPrivacy,
    dataSignalTerms,
    dataSignalFaq,
    dataSignalSendOtp,
    dataSignalAccessToken,
    dataSignalProfile,
    dataSignalKyc,
    dataSignalShopList,
    dataSignalTransactionTypes,
    walletSignal,
    dataSignalGamePlatform,
    dataSignalGameList,
    dataSignalAddGameFav,
    dataSignalGameFavList,
    dataSignalDeleteGameFav,
    dataSignalGameBetList,
    dataSignalGameLaunch,
    dataSignalFinanceWallet,
    dataSignalFinanceAccountList,
    dataSignalUpdateFinanceAccount,
    dataSignalDeleteFinanceAccount,
    dataSignalFinanceChannelList,
    dataSignalFinancePaymentDeposit,
    dataSignalFinancePaymentWithdraw,
    dataSignalFinanceDepositList,
    dataSignalFinanceDepositDetail,
    dataSignalFinanceWithdrawList,
    dataSignalFinanceWithdrawDetail,
    dataSignalIndexCache,
    dataSignalAddAccounts,
    dataSignalFetchAccounts,
    dataSignalDeleteAccounts,
    dataSignalWithDraw,
    dataSignalBlindBox,
    dataBlindBoxClaim,
    dataSignalVipRule,
    dataSignalLazadaminiLogin,
    dataSignalBuriedPointDetail,
}

export const ApiSignalFetchers = {
    fetchDataPromoList,
    fetchDataSignalPrivacy,
    fetchDataSignalTerms,
    fetchDataSignalFaq,
    fetchDataSignalSendOtp,
    fetchDataSignalLoginWithOtp,
    fetchDataSignalLoginWithPassword,
    fetchDataSignalLogout,
    fetchDataSignalRefreshToken,
    fetchDataSignalProfile,
    fetchDataSignalKyc,
    fetchDataSignalShopList,
    fetchWalletSignal,
    fetchDataSignalTransactionTypes,
    fetchDataSignalIndexCache,
    fetchDataSignalGamePlatform,
    fetchDataSignalGameList,
    fetchDataSignalAddGameFav,
    fetchDataSignalGameFavList,
    fetchDataSignalDeleteGameFav,
    fetchDataSignalGameBetList,
    fetchDataSignalGameLaunch,
    fetchDataSignalFinanceWallet,
    fetchDataSignalFinanceAccountList,
    fetchDataSignalUpdateFinanceAccount,
    fetchDataSignalDeleteFinanceAccount,
    fetchDataSignalFinanceChannelList,
    fetchDataSignalFinancePaymentDeposit,
    fetchDataSignalFinancePaymentWithdraw,
    fetchDataSignalFinanceDepositList,
    fetchDataSignalFinanceDepositDetail,
    fetchDataSignalFinanceWithdrawList,
    fetchDataSignalFinanceWithdrawDetail,
    addDataSignalAccounts,
    fetchDataSignalAccounts,
    deleteDataSignalAccounts,
    fetchDataSignalWithdraw,
    fetchDataSignalBlindBox,
    fetchDataBlindBoxClaim,
    fetchDataSignalVipRule,
    fetchDataSignalLazadaminiLogin,
    fetchDataSignalBuriedPointDetail,
}

export default ApiSignals

const KYC_STATUS_NAMES = {
    0: "unset",
    1: "basic_account",
    2: "under_review",
    3: "rejected",
    4: "basic_kyc",
    5: "fully_kyc",
    6: "frozen",
    7: "block",
    8: "test_account",
} as const

const kycSourceData = computed(() => {
    const profile = dataSignalProfile.value
    const kyc = dataSignalKyc.value

    const status: Kyc.Status = (() => {
        return KYC_STATUS_NAMES[profile?.kyc_status ?? 0]
    })()

    const output = {
        profile: dataSignalProfile.value,
        kyc: {
            ...kyc,
            status,
        },
    }

    logger.debug("kycSourceData", output)
    return output
})

const kycDataFetched = computed(() => {
    return !!dataSignalProfile.value && !!dataSignalKyc.value
})

const kycExpiredAt = computed(() => {
    const createdAtDayjs = dayjs(dataSignalProfile.value?.created_at)
    const endDayjs = createdAtDayjs.add(3, "day")
    return endDayjs
})

const hoursAfterMemberCreated = computed(() => {
    const createdAtDayjs = dayjs(dataSignalProfile.value?.created_at)
    const diff = dayjs().diff(createdAtDayjs, "hours")
    return diff
})

const hoursAfterMemberCreated24Hours = computed(() => {
    return hoursAfterMemberCreated.value > 24
})

const hoursAfterMemberCreated48Hours = computed(() => {
    return hoursAfterMemberCreated.value > 48
})

const hoursAfterMemberCreated72Hours = computed(() => {
    return hoursAfterMemberCreated.value > 72
})

const kycLeftSeconds = computed(() => {
    const expiredAt = kycExpiredAt.value
    if (!expiredAt) return 0
    const diff = expiredAt.diff(dayjs(), "seconds")
    return diff > 0 ? diff : 0
})

const kycOcrStatus = computed(() => {
    const kyc = dataSignalKyc.value
    if (!kyc?.ocr_status) return 0
    return kyc.ocr_status
})

const isUserLogin = computed(() => {
    if (dataSignalProfile.value?.username) {
        return true
    }
    return false
})

export const searchParamsSignal = signal(new URLSearchParams(window.location.search))
const kycOcrCallbackParams = computed(() => {
    const params = searchParamsSignal.value
    const payloadEncoded = params.get("payload")

    if (!payloadEncoded) {
        return null
    }

    try {
        const payload = PayloadFormatter.decode(
            payloadEncoded,
        ) as unknown as Kyc.Data.OcrCallbackParams
        const success = params.get("success") === "true"

        return {
            ...payload,
            success,
        }
    } catch (error) {
        logger.debug("解析 OCR 回調參數失敗", error)
        return null
    }
})

export const BusinessSignals = {
    kycSourceData,
    kycExpiredAt,
    hoursAfterMemberCreated,
    hoursAfterMemberCreated24Hours,
    hoursAfterMemberCreated48Hours,
    hoursAfterMemberCreated72Hours,
    kycLeftSeconds,
    kycDataFetched,
    kycOcrStatus,
    kycOcrCallbackParams,
    isUserLogin,
}
